import React, { useMemo, useState } from "react";
import { Form, Row, Col, DatePicker, Typography, Table, Select, message } from 'antd';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { useDispatch } from "react-redux";
import { getRangeOptions, getPaginationDetails, getDefaultPageSize } from './reports.module';
import { getOccupancyReportAction } from './../../actions/reports.action';
import { getAPIDateFormat, getLocalDateAndTime } from './../../utils/date.utils';
import { isValid, isValidArray } from "../../utils/utilities";
import { Printer } from './../../components/custom-components/custom-component';
import { loadingAction } from './../../actions/loading-actions';
import './occupancy.report.scss';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const allowedMaxDaysRange = 31;
const xs = 24, sm = 24, md = 12, lg = 12, xl = 12, xxl = 12;

const NO_VEHICLE = 'Not allotted';

const TRIP_STATUS = { ACTIVE: 'ACTIVE', CANCELLED: 'CANCELLED', ALL: 'ALL' };
const TRIP_STATUS_LABELS = { ACTIVE: 'Active', CANCELLED: 'Cancelled', ALL: 'Active + Cancelled' };

// Occupancy bands: lower bound inclusive, upper bound exclusive.
const OCC_BAND_ALL = 'all';
const OCC_BANDS = [
    { value: OCC_BAND_ALL, label: 'All occupancy' },
    { value: 'low', label: 'Low (below 25%)', min: 0, max: 25 },
    { value: 'medium', label: 'Medium (25% – 75%)', min: 25, max: 75 },
    { value: 'high', label: 'High (75% and above)', min: 75, max: 101 },
];

// ============================================================
// Formatters / helpers
// ============================================================
const toNum = (n) => {
    const v = Number(n);
    return isNaN(v) ? 0 : v;
};
const fmtINR = (n) => {
    if (n == null || isNaN(n)) return '—';
    const num = Number(n);
    const hasDecimal = num % 1 !== 0;
    return '₹' + num.toLocaleString('en-IN', hasDecimal ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {});
};
const fmtPct = (n) => `${toNum(n).toFixed(2)}%`;
const fmtCount = (n) => toNum(n).toLocaleString('en-IN');
const fmtDate = (d) => isValid(d) ? moment(d).format('DD MMM YYYY') : '—';
const fmtTime = (t) => isValid(t) ? String(t).substring(0, 5) : '';
const getOccupancyBand = (occ) => (occ >= 75 ? 'high' : (occ >= 25 ? 'medium' : 'low'));
const getVehicleLabel = (row) => row.vehicleNumber || NO_VEHICLE;
// Schedules may have a blank name; fall back to the description so a service is always identifiable.
const hasText = (v) => isValid(v) && String(v).trim() !== '';
const getServiceLabel = (row) => (hasText(row.serviceName) ? row.serviceName : (hasText(row.description) ? row.description : `Schedule ${row.scheduleId}`));
const getServiceSubLabel = (row) => (hasText(row.serviceName) && hasText(row.description) ? row.description : null);

export const buildSummary = (rows) => {
    const summary = {
        services: 0, totalSeats: 0, bookedSeats: 0, blockedSeats: 0, availableSeats: 0,
        collectionValue: 0, potentialLoss: 0, occupancy: 0,
    };
    if (isValidArray(rows)) {
        rows.forEach((row) => {
            summary.services += 1;
            summary.totalSeats += toNum(row.totalSeats);
            summary.bookedSeats += toNum(row.bookedSeats);
            summary.blockedSeats += toNum(row.blockedSeats);
            summary.availableSeats += toNum(row.availableSeats);
            summary.collectionValue += toNum(row.collectionValue);
            summary.potentialLoss += toNum(row.potentialLoss);
        });
    }
    summary.collectionValue = Math.round(summary.collectionValue * 100) / 100;
    summary.potentialLoss = Math.round(summary.potentialLoss * 100) / 100;
    summary.occupancy = summary.totalSeats > 0
        ? Math.round((summary.bookedSeats / summary.totalSeats) * 10000) / 100
        : 0;
    return summary;
};

export const applyRefineFilters = (rows, { scheduleIds, isSelectAllSchedules, vehicleNumbers, isSelectAllVehicles, occupancyBand }) => {
    if (!isValidArray(rows)) return [];
    const band = OCC_BANDS.find((b) => b.value === occupancyBand);
    const filterSchedules = !isSelectAllSchedules && isValidArray(scheduleIds) && scheduleIds.length > 0;
    const filterVehicles = !isSelectAllVehicles && isValidArray(vehicleNumbers) && vehicleNumbers.length > 0;
    return rows.filter((row) => {
        if (filterSchedules && !scheduleIds.includes(row.scheduleId)) return false;
        if (filterVehicles && !vehicleNumbers.includes(getVehicleLabel(row))) return false;
        if (band && band.value !== OCC_BAND_ALL) {
            const occ = toNum(row.occupancy);
            if (occ < band.min || occ >= band.max) return false;
        }
        return true;
    });
};

// ============================================================
// Inline icons (same set as the sales / blocked seats reports)
// ============================================================
const Icon = {
    search: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="7" cy="7" r="5"/><path d="m11 11 3 3"/></svg>,
    download: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 2v9m-3-3 3 3 3-3M2 13h12"/></svg>,
    caretRight: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m6 4 4 4-4 4"/></svg>,
    run: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5 3l8 5-8 5z"/></svg>,
};

// ============================================================
// Columns used for Excel export + the print preview (full detail)
// ============================================================
const getExportColumns = (page, pageSize) => [
    { title: 'SN', key: 'index', render: (text, record, index) => (page - 1) * pageSize + index + 1 },
    { title: 'Service', key: 'serviceName', dataIndex: 'serviceName', render: (text, record) => getServiceLabel(record) },
    { title: 'Description', key: 'description', dataIndex: 'description', render: (text, record) => getServiceSubLabel(record) || '' },
    { title: 'Travel date', key: 'travelDate', dataIndex: 'travelDate', render: (text) => fmtDate(text) },
    { title: 'Departure', key: 'startTime', dataIndex: 'startTime', render: (text) => fmtTime(text) },
    { title: 'Vehicle', key: 'vehicleNumber', dataIndex: 'vehicleNumber', render: (text, record) => getVehicleLabel(record) },
    { title: 'Status', key: 'status', dataIndex: 'status' },
    { title: 'Occupancy %', key: 'occupancy', dataIndex: 'occupancy', render: (text) => toNum(text) },
    { title: 'Booked seats', key: 'bookedSeats', dataIndex: 'bookedSeats', render: (text) => toNum(text) },
    { title: 'Blocked seats', key: 'blockedSeats', dataIndex: 'blockedSeats', render: (text) => toNum(text) },
    { title: 'Available seats', key: 'availableSeats', dataIndex: 'availableSeats', render: (text) => toNum(text) },
    { title: 'Total seats', key: 'totalSeats', dataIndex: 'totalSeats', render: (text) => toNum(text) },
    { title: 'Collection value', key: 'collectionValue', dataIndex: 'collectionValue', render: (text) => toNum(text) },
    { title: 'Potential loss', key: 'potentialLoss', dataIndex: 'potentialLoss', render: (text) => toNum(text) },
];

const generateExcel = (filters, rows, summary, columns, labels) => {
    const dataRows = [];
    if (isValidArray(rows) && rows.length > 0) {
        rows.forEach((row, index) => {
            const rowData = [];
            columns.forEach((column) => {
                if (column.key === 'index') {
                    rowData.push(index + 1);
                } else if (column.render) {
                    rowData.push(column.render(row[column.dataIndex], row, index));
                } else {
                    rowData.push(row[column.dataIndex] == null ? '' : row[column.dataIndex]);
                }
            });
            dataRows.push(rowData);
        });
    }

    const worksheet = XLSX.utils.aoa_to_sheet([
        [],
        [],
        ["Report", "From Date", "To Date", "Trip status", "Service", "Vehicle", "Occupancy band", "Generated day/time"],
        ["Occupancy report", filters.fromDate, filters.toDate, TRIP_STATUS_LABELS[filters.tripStatus] || filters.tripStatus,
            labels.scheduleLabel, labels.vehicleLabel, labels.bandLabel, getLocalDateAndTime(new Date())],
        [],
        ["Services", "Total seats", "Booked seats", "Blocked seats", "Available seats", "Occupancy %", "Collection value", "Potential loss"],
        [summary.services, summary.totalSeats, summary.bookedSeats, summary.blockedSeats, summary.availableSeats,
            summary.occupancy, summary.collectionValue, summary.potentialLoss],
        [],
        columns.map((column) => column.title),
        ...dataRows,
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Occupancy Report');
    XLSX.writeFile(workbook, `occupancy-report-${filters.fromDate}-to-${filters.toDate}.xlsx`);
};

// ============================================================
// Sub-components
// ============================================================
function PageHeader({ onExport, canExport, printerNode }) {
    return (
        <div className="pageheader">
            <div>
                <div className="breadcrumb">
                    <span>Reports</span>{Icon.caretRight}<span style={{ color: 'var(--ink-2)' }}>Occupancy report</span>
                </div>
                <h1>Occupancy Report</h1>
            </div>
            <div className="pageheader-actions">
                {printerNode}
                <button type="button" className="btn primary" disabled={!canExport} onClick={onExport}>
                    {Icon.download}Export
                </button>
            </div>
        </div>
    );
}

function FilterCard({ form, tripStatus, setTripStatus, onSubmit, onReset, running }) {
    const handleSubmit = (values) => onSubmit({ ...values, tripStatus });

    return (
        <div className="card">
            <Form form={form} onFinish={handleSubmit}>
                <div className="filter-grid">
                    <div className="field field-rangepicker-wrap" style={{ gridColumn: '1 / span 5', gridRow: 1 }}>
                        <div className="field-label">Travel date range</div>
                        <Form.Item name="dateRange" rules={[
                            { required: true, message: 'Please select a travel date range' },
                            () => ({
                                validator(_, value) {
                                    if (!value || (value[1].diff(value[0], "days") <= allowedMaxDaysRange)) return Promise.resolve();
                                    return Promise.reject(new Error(`Please select a date range within ${allowedMaxDaysRange} days`));
                                },
                            }),
                        ]}>
                            <RangePicker ranges={getRangeOptions(allowedMaxDaysRange)} format="DD MMM YYYY" />
                        </Form.Item>
                    </div>

                    <div className="field" style={{ gridColumn: '6 / span 4', gridRow: 1 }}>
                        <div className="field-label">Trip status</div>
                        <div className="segmented">
                            <button type="button"
                                className={tripStatus === TRIP_STATUS.ACTIVE ? 'active' : ''}
                                onClick={() => setTripStatus(TRIP_STATUS.ACTIVE)}>Active</button>
                            <button type="button"
                                className={tripStatus === TRIP_STATUS.CANCELLED ? 'active' : ''}
                                onClick={() => setTripStatus(TRIP_STATUS.CANCELLED)}>Cancelled</button>
                            <button type="button"
                                className={tripStatus === TRIP_STATUS.ALL ? 'active' : ''}
                                onClick={() => setTripStatus(TRIP_STATUS.ALL)}>All</button>
                        </div>
                    </div>

                    <div className="filter-actions" style={{ gridColumn: '1 / -1', gridRow: 2, display: 'flex', gap: 8, justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                        <button type="button" className="btn" onClick={onReset} disabled={running}>Reset</button>
                        <button type="submit" className="btn primary" disabled={running}>
                            {Icon.run}{running ? 'Running…' : 'Run report'}
                        </button>
                    </div>
                </div>
            </Form>
        </div>
    );
}

function RefineBar({
    scheduleOptions, selectedSchedules, isSelectAllSchedules, onScheduleChange,
    vehicleOptions, selectedVehicles, isSelectAllVehicles, onVehicleChange,
    occupancyBand, onOccupancyBandChange,
}) {
    const allScheduleIds = useMemo(
        () => isValidArray(scheduleOptions) ? scheduleOptions.map((s) => s.value) : [],
        [scheduleOptions]
    );
    const allVehicles = useMemo(
        () => isValidArray(vehicleOptions) ? vehicleOptions : [],
        [vehicleOptions]
    );

    const handleScheduleChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allScheduleIds.length > 0 && selected.length >= allScheduleIds.length);
        onScheduleChange({ scheduleIds: selectAll ? [] : selected, isSelectAllSchedules: selectAll });
    };

    const handleVehicleChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allVehicles.length > 0 && selected.length >= allVehicles.length);
        onVehicleChange({ vehicleNumbers: selectAll ? [] : selected, isSelectAllVehicles: selectAll });
    };

    const scheduleValue = isSelectAllSchedules ? [] : (selectedSchedules || []);
    const vehicleValue = isSelectAllVehicles ? [] : (selectedVehicles || []);
    const hasActiveFilter = !isSelectAllSchedules || !isSelectAllVehicles || occupancyBand !== OCC_BAND_ALL;

    return (
        <div className="refine-bar">
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by service</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allScheduleIds.length === 0 ? 'No services in result' : 'All services'}
                    value={scheduleValue}
                    onChange={handleScheduleChange}
                    maxTagCount={2}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="children"
                    filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allScheduleIds.length === 0}
                    style={{ width: '100%' }}
                >
                    {(scheduleOptions || []).map((s) => <Option key={s.value} value={s.value} title={s.description}>{s.label}</Option>)}
                </Select>
            </div>
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by vehicle</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allVehicles.length === 0 ? 'No vehicles in result' : 'All vehicles'}
                    value={vehicleValue}
                    onChange={handleVehicleChange}
                    maxTagCount={2}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="children"
                    filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allVehicles.length === 0}
                    style={{ width: '100%' }}
                >
                    {allVehicles.map((v) => <Option key={v} value={v}>{v}</Option>)}
                </Select>
            </div>
            <div className="field field-select-wrap" style={{ width: 220 }}>
                <div className="field-label">Occupancy</div>
                <Select value={occupancyBand} onChange={onOccupancyBandChange} style={{ width: '100%' }}>
                    {OCC_BANDS.map((b) => <Option key={b.value} value={b.value}>{b.label}</Option>)}
                </Select>
            </div>
            {hasActiveFilter && (
                <button type="button" className="btn" onClick={() => {
                    handleScheduleChange([]);
                    handleVehicleChange([]);
                    onOccupancyBandChange(OCC_BAND_ALL);
                }}>Clear</button>
            )}
        </div>
    );
}

function FilterSummaryChips({ filters, generatedAt, scheduleLabel, vehicleLabel, bandLabel, hasReport }) {
    if (!hasReport) return null;
    const dateLabel = filters.fromDate && filters.toDate ? `${filters.fromDate} – ${filters.toDate}` : '—';
    return (
        <div className="filter-summary">
            <span className="chip"><span className="label">Travel</span><strong>{dateLabel}</strong></span>
            <span className="chip"><span className="label">Trips</span><strong>{TRIP_STATUS_LABELS[filters.tripStatus] || filters.tripStatus}</strong></span>
            <span className="chip"><span className="label">Service</span><strong>{scheduleLabel || 'All'}</strong></span>
            <span className="chip"><span className="label">Vehicle</span><strong>{vehicleLabel || 'All'}</strong></span>
            <span className="chip"><span className="label">Occupancy</span><strong>{bandLabel}</strong></span>
            {generatedAt && <span className="gen">Generated {generatedAt}</span>}
        </div>
    );
}

function HeroMetric({ summary }) {
    return (
        <div className="hero-metric">
            <div>
                <div className="l">Overall occupancy</div>
                <div className="v">{fmtPct(summary.occupancy)}</div>
                <div className="hero-counts">
                    <span className="hero-count confirmed">
                        <strong>{fmtCount(summary.bookedSeats)}</strong> booked
                    </span>
                    <span className="hero-count blocked">
                        <strong>{fmtCount(summary.blockedSeats)}</strong> blocked
                    </span>
                    <span className="hero-count available">
                        <strong>{fmtCount(summary.availableSeats)}</strong> available
                    </span>
                </div>
                <div className="formula">
                    Booked seats ÷ Total seats across {fmtCount(summary.services)} {summary.services === 1 ? 'service' : 'services'} ({fmtCount(summary.totalSeats)} seats)
                </div>
            </div>
            <div className="right">
                <div className="pair">
                    <div className="stat">
                        <div className="l">Collection value</div>
                        <div className="v">{fmtINR(summary.collectionValue)}</div>
                        <div className="sub">Amount paid for booked seats</div>
                    </div>
                    <div className="stat">
                        <div className="l">Potential loss</div>
                        <div className="v">{fmtINR(summary.potentialLoss)}</div>
                        <div className="sub">Available seats at list fare</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function SummaryCard({ summary }) {
    const rows = [
        { code: 'A', metric: 'Services', value: fmtCount(summary.services) },
        { code: 'B', metric: 'Total seats', value: fmtCount(summary.totalSeats) },
        { code: 'C', metric: 'Booked seats', value: fmtCount(summary.bookedSeats) },
        { code: 'D', metric: 'Blocked seats', value: fmtCount(summary.blockedSeats) },
        { code: 'E', metric: 'Available seats (B − C − D)', value: fmtCount(summary.availableSeats) },
        { code: 'F', metric: 'Occupancy (C ÷ B)', value: fmtPct(summary.occupancy), subtotal: true },
        { code: 'G', metric: 'Collection value of booked seats', value: fmtINR(summary.collectionValue) },
        { code: 'H', metric: 'Potential loss of available seats', value: fmtINR(summary.potentialLoss) },
    ];
    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Occupancy Summary</div>
            </div>
            <HeroMetric summary={summary} />
            <div className="summary-section">
                <div className="summary-section-title">Seat Summary</div>
                <div className="summary-table kv">
                    <div className="h">Metric</div>
                    <div className="h right">Value</div>
                    {rows.map((r) => (
                        <div key={r.code} className={`row ${r.subtotal ? 'subtotal' : ''}`}>
                            <div className="cell metric"><span className="code">{r.code}.</span>{r.metric}</div>
                            <div className="cell value">{r.value}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const OccupancyCell = ({ value }) => {
    const occ = Math.min(100, Math.max(0, toNum(value)));
    const band = getOccupancyBand(occ);
    return (
        <div className={`occ-cell ${band}`}>
            <div className="occ-track"><div className="occ-fill" style={{ width: `${occ}%` }} /></div>
            <span className="occ-value">{fmtPct(value)}</span>
        </div>
    );
};

const buildDisplayColumns = (page, pageSize) => [
    {
        title: '#', key: 'index', width: 44,
        render: (_t, _r, index) => <span className="tx-date">{(page - 1) * pageSize + index + 1}</span>,
    },
    {
        title: 'Service', key: 'serviceName', width: '24%',
        sorter: (a, b) => getServiceLabel(a).localeCompare(getServiceLabel(b)),
        render: (_t, record) => (
            <div className="tx-route">
                <div className="ot">{getServiceLabel(record)}</div>
                {getServiceSubLabel(record) && <div className="name">{getServiceSubLabel(record)}</div>}
            </div>
        ),
    },
    {
        title: 'Travel', key: 'travelDate',
        sorter: (a, b) => String(a.travelDate || '').localeCompare(String(b.travelDate || '')),
        render: (_t, record) => (
            <span className="tx-date">{fmtDate(record.travelDate)}{record.startTime ? ` · ${fmtTime(record.startTime)}` : ''}</span>
        ),
    },
    {
        title: 'Vehicle', key: 'vehicleNumber',
        render: (_t, record) => <span className="tx-date">{getVehicleLabel(record)}</span>,
    },
    {
        title: 'Status', key: 'status',
        render: (_t, record) => (
            <span className={`tx-status ${record.status === 'Active' ? 'active' : 'cancelled'}`}>{record.status}</span>
        ),
    },
    {
        title: 'Occupancy', key: 'occupancy',
        sorter: (a, b) => toNum(a.occupancy) - toNum(b.occupancy),
        render: (_t, record) => <OccupancyCell value={record.occupancy} />,
    },
    {
        title: 'Booked', key: 'bookedSeats', align: 'right', className: 'col-amount',
        sorter: (a, b) => toNum(a.bookedSeats) - toNum(b.bookedSeats),
        render: (_t, record) => <span className="tx-count">{fmtCount(record.bookedSeats)}</span>,
    },
    {
        title: 'Blocked', key: 'blockedSeats', align: 'right', className: 'col-amount',
        sorter: (a, b) => toNum(a.blockedSeats) - toNum(b.blockedSeats),
        render: (_t, record) => <span className="tx-count">{fmtCount(record.blockedSeats)}</span>,
    },
    {
        title: 'Available', key: 'availableSeats', align: 'right', className: 'col-amount',
        sorter: (a, b) => toNum(a.availableSeats) - toNum(b.availableSeats),
        render: (_t, record) => <span className="tx-count">{fmtCount(record.availableSeats)}</span>,
    },
    {
        title: 'Total', key: 'totalSeats', align: 'right', className: 'col-amount',
        sorter: (a, b) => toNum(a.totalSeats) - toNum(b.totalSeats),
        render: (_t, record) => <span className="tx-count">{fmtCount(record.totalSeats)}</span>,
    },
    {
        title: 'Collection', key: 'collectionValue', align: 'right', className: 'col-amount',
        sorter: (a, b) => toNum(a.collectionValue) - toNum(b.collectionValue),
        render: (_t, record) => <span className="tx-amount">{fmtINR(record.collectionValue)}</span>,
    },
    {
        title: 'Potential loss', key: 'potentialLoss', align: 'right', className: 'col-amount',
        sorter: (a, b) => toNum(a.potentialLoss) - toNum(b.potentialLoss),
        render: (_t, record) => <span className="tx-amount">{fmtINR(record.potentialLoss)}</span>,
    },
];

export function ServicesCard({ rows, summary, page, setPage, paginationSize, setPaginationSize }) {
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('occupancy_low');

    const data = useMemo(() => {
        if (!isValidArray(rows)) return [];
        const q = search.trim().toLowerCase();
        let r = rows;
        if (q) {
            r = r.filter((x) => {
                const fields = [getServiceLabel(x), x.description, x.origin, x.destination, getVehicleLabel(x), fmtDate(x.travelDate), x.status];
                return fields.some((f) => f && String(f).toLowerCase().includes(q));
            });
        }
        const sorted = [...r];
        switch (sort) {
            case 'occupancy_low':
                sorted.sort((a, b) => toNum(a.occupancy) - toNum(b.occupancy)); break;
            case 'occupancy_high':
                sorted.sort((a, b) => toNum(b.occupancy) - toNum(a.occupancy)); break;
            case 'travel_date':
                sorted.sort((a, b) => String(a.travelDate || '').localeCompare(String(b.travelDate || '')) || String(a.startTime || '').localeCompare(String(b.startTime || ''))); break;
            case 'collection_high':
                sorted.sort((a, b) => toNum(b.collectionValue) - toNum(a.collectionValue)); break;
            case 'loss_high':
                sorted.sort((a, b) => toNum(b.potentialLoss) - toNum(a.potentialLoss)); break;
            default: break;
        }
        return sorted;
    }, [rows, search, sort]);

    const columns = useMemo(() => buildDisplayColumns(page, paginationSize), [page, paginationSize]);
    const visibleSummary = useMemo(() => buildSummary(data), [data]);

    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Services</div>
                <div className="tabs">
                    <button type="button" className="active">
                        Services <span className="count">{fmtCount(data.length)}</span>
                    </button>
                </div>
            </div>
            <div className="card-sub">
                One row per service (trip) travelling in the selected period. Occupancy is booked seats over the total seats in the layout; potential loss is the list fare of seats still available.
            </div>

            <div className="table-toolbar">
                <div className="search">
                    {Icon.search}
                    <input
                        placeholder="Search by service, route, vehicle, travel date…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="table-toolbar-right">
                    <span>Sort:</span>
                    <Select value={sort} onChange={setSort} size="small" style={{ width: 210 }} bordered={true}>
                        <Option value="occupancy_low">Occupancy (lowest first)</Option>
                        <Option value="occupancy_high">Occupancy (highest first)</Option>
                        <Option value="travel_date">Travel date</Option>
                        <Option value="collection_high">Highest collection</Option>
                        <Option value="loss_high">Highest potential loss</Option>
                    </Select>
                </div>
            </div>

            <Table
                className="tx-table"
                rowKey={(r) => r.tripId}
                columns={columns}
                dataSource={data}
                pagination={getPaginationDetails(true, setPage, paginationSize, setPaginationSize)}
                locale={{ emptyText: <div className="empty-state">No services match the current filters.</div> }}
                size="middle"
                summary={() => (
                    <Table.Summary>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>{fmtCount(visibleSummary.services)} services</Table.Summary.Cell>
                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                            <Table.Summary.Cell index={3}></Table.Summary.Cell>
                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                            <Table.Summary.Cell index={5}>{fmtPct(visibleSummary.occupancy)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={6} align="right">{fmtCount(visibleSummary.bookedSeats)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={7} align="right">{fmtCount(visibleSummary.blockedSeats)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={8} align="right">{fmtCount(visibleSummary.availableSeats)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={9} align="right">{fmtCount(visibleSummary.totalSeats)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={10} align="right">{fmtINR(visibleSummary.collectionValue)}</Table.Summary.Cell>
                            <Table.Summary.Cell index={11} align="right">{fmtINR(visibleSummary.potentialLoss)}</Table.Summary.Cell>
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
        </div>
    );
}

// ============================================================
// Main component
// ============================================================
const getInitialFilters = () => ({
    fromDate: undefined, toDate: undefined, tripStatus: TRIP_STATUS.ACTIVE,
    scheduleIds: [], isSelectAllSchedules: true,
    vehicleNumbers: [], isSelectAllVehicles: true,
    occupancyBand: OCC_BAND_ALL,
});

export const OccupancyReport = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [showReport, setShowReport] = useState(false);
    const [running, setRunning] = useState(false);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [tripStatus, setTripStatus] = useState(TRIP_STATUS.ACTIVE);
    const [rows, setRows] = useState(undefined);

    const [page, setPage] = useState(1);
    const [paginationSize, setPaginationSize] = useState(getDefaultPageSize);
    const [filters, setFilters] = useState(getInitialFilters());

    const setLoading = (bool) => dispatch(loadingAction(bool));

    const onSuccess = (data) => {
        const report = isValidArray(data.occupancyReport) ? data.occupancyReport : [];
        setRows(report);
        setPage(1);
        setFilters((f) => ({
            ...f,
            scheduleIds: [], isSelectAllSchedules: true,
            vehicleNumbers: [], isSelectAllVehicles: true,
            occupancyBand: OCC_BAND_ALL,
        }));
        setGeneratedAt(getLocalDateAndTime(new Date()));
        setShowReport(true);
        setRunning(false);
        setLoading(false);
        if (report.length === 0) {
            message.info('No services found for the selected period');
        }
    };

    const onFailure = (errorMessage) => {
        setRunning(false);
        setLoading(false);
        message.error(errorMessage || 'Failed to load occupancy report');
    };

    const onRunReport = (values) => {
        const { dateRange } = values;
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        setShowReport(false);
        setRunning(true);
        setLoading(true);
        setFilters((f) => ({ ...f, fromDate, toDate, tripStatus }));
        getOccupancyReportAction(fromDate, toDate, tripStatus, onSuccess, onFailure);
    };

    const onReset = () => {
        form.resetFields();
        setTripStatus(TRIP_STATUS.ACTIVE);
        setRows(undefined);
        setShowReport(false);
        setGeneratedAt(null);
        setPage(1);
        setFilters(getInitialFilters());
    };

    // Options for the refine bar are derived from the loaded result.
    const scheduleOptions = useMemo(() => {
        if (!isValidArray(rows)) return [];
        const byId = {};
        rows.forEach((row) => {
            if (isValid(row.scheduleId) && !byId[row.scheduleId]) {
                byId[row.scheduleId] = {
                    value: row.scheduleId,
                    label: getServiceLabel(row),
                    description: row.description,
                };
            }
        });
        return Object.values(byId).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }, [rows]);

    const vehicleOptions = useMemo(() => {
        if (!isValidArray(rows)) return [];
        return [...new Set(rows.map(getVehicleLabel))].sort();
    }, [rows]);

    const filteredRows = useMemo(() => applyRefineFilters(rows, filters), [rows, filters]);
    const summary = useMemo(() => buildSummary(filteredRows), [filteredRows]);

    const scheduleLabel = filters.isSelectAllSchedules ? 'All'
        : (isValidArray(filters.scheduleIds) && filters.scheduleIds.length > 0
            ? (filters.scheduleIds.length <= 2
                ? filters.scheduleIds.map((id) => (scheduleOptions.find((s) => s.value === id) || {}).label || id).join(', ')
                : `${filters.scheduleIds.length} selected`)
            : 'All');
    const vehicleLabel = filters.isSelectAllVehicles ? 'All'
        : (isValidArray(filters.vehicleNumbers) && filters.vehicleNumbers.length > 0
            ? (filters.vehicleNumbers.length <= 2 ? filters.vehicleNumbers.join(', ') : `${filters.vehicleNumbers.length} selected`)
            : 'All');
    const bandLabel = (OCC_BANDS.find((b) => b.value === filters.occupancyBand) || OCC_BANDS[0]).label;

    const onExport = () => {
        if (!isValidArray(filteredRows) || filteredRows.length === 0) {
            message.warning('No data to export');
            return;
        }
        generateExcel(filters, filteredRows, summary, getExportColumns(1, getDefaultPageSize), { scheduleLabel, vehicleLabel, bandLabel });
    };

    const printerNode = (
        <Printer
            showComponent={false}
            zoomValue={0.8}
            renderComponent={() => (
                <RenderReport
                    filters={filters}
                    rows={filteredRows}
                    summary={summary}
                    scheduleLabel={scheduleLabel}
                    vehicleLabel={vehicleLabel}
                    bandLabel={bandLabel}
                />
            )}
        />
    );

    return (
        <div className="report-inner-div sales-report-v2 occupancy-report">
            <PageHeader onExport={onExport} canExport={showReport} printerNode={printerNode} />
            <FilterCard
                form={form}
                tripStatus={tripStatus}
                setTripStatus={setTripStatus}
                onSubmit={onRunReport}
                onReset={onReset}
                running={running}
            />
            {showReport && (
                <RefineBar
                    scheduleOptions={scheduleOptions}
                    selectedSchedules={filters.scheduleIds}
                    isSelectAllSchedules={filters.isSelectAllSchedules}
                    onScheduleChange={({ scheduleIds, isSelectAllSchedules }) => { setPage(1); setFilters((f) => ({ ...f, scheduleIds, isSelectAllSchedules })); }}
                    vehicleOptions={vehicleOptions}
                    selectedVehicles={filters.vehicleNumbers}
                    isSelectAllVehicles={filters.isSelectAllVehicles}
                    onVehicleChange={({ vehicleNumbers, isSelectAllVehicles }) => { setPage(1); setFilters((f) => ({ ...f, vehicleNumbers, isSelectAllVehicles })); }}
                    occupancyBand={filters.occupancyBand}
                    onOccupancyBandChange={(occupancyBand) => { setPage(1); setFilters((f) => ({ ...f, occupancyBand })); }}
                />
            )}
            <FilterSummaryChips
                filters={filters}
                generatedAt={generatedAt}
                scheduleLabel={scheduleLabel}
                vehicleLabel={vehicleLabel}
                bandLabel={bandLabel}
                hasReport={showReport}
            />
            {showReport && <SummaryCard summary={summary} />}
            {showReport && (
                <ServicesCard
                    rows={filteredRows}
                    summary={summary}
                    page={page}
                    setPage={setPage}
                    paginationSize={paginationSize}
                    setPaginationSize={setPaginationSize}
                />
            )}
        </div>
    );
};

// ============================================================
// Print layout — classic bordered tables rendered by the Printer.
// ============================================================
const summaryPrintColumns = [
    { title: 'Services', key: 'services', dataIndex: 'services' },
    { title: 'Total seats', key: 'totalSeats', dataIndex: 'totalSeats' },
    { title: 'Booked seats', key: 'bookedSeats', dataIndex: 'bookedSeats' },
    { title: 'Blocked seats', key: 'blockedSeats', dataIndex: 'blockedSeats' },
    { title: 'Available seats', key: 'availableSeats', dataIndex: 'availableSeats' },
    { title: 'Occupancy', key: 'occupancy', dataIndex: 'occupancy', render: (text) => fmtPct(text) },
    { title: 'Collection value', key: 'collectionValue', dataIndex: 'collectionValue' },
    { title: 'Potential loss', key: 'potentialLoss', dataIndex: 'potentialLoss' },
];

export const RenderReport = ({ filters, rows, summary, scheduleLabel, vehicleLabel, bandLabel }) => {
    const columns = getExportColumns(1, getDefaultPageSize);

    return (
        <div className="occupancy-print">
            <Row justify="center" gutter={[16, 16]} className="occupancy-print-title">
                <Col><Text strong>Occupancy report</Text></Col>
            </Row>
            <Row gutter={[16, 0]} justify={"space-between"}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>From date : </Text>{filters.fromDate}</Text>
                </Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>To date : </Text>{filters.toDate}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 0]} justify={"space-between"}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Trip status : </Text>{TRIP_STATUS_LABELS[filters.tripStatus] || filters.tripStatus}</Text>
                </Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Generated day/time : </Text>{getLocalDateAndTime(new Date())}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 0]} justify={"space-between"}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Service : </Text>{scheduleLabel}</Text>
                </Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Vehicle : </Text>{vehicleLabel}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col>
                    <Text><Text strong>Occupancy : </Text>{bandLabel}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 16]} className="marginTop occupancy-print-summary">
                <Col span={24}>
                    <Table
                        className="bordered-table"
                        columns={summaryPrintColumns}
                        dataSource={[{ ...summary, key: 'summary' }]}
                        pagination={false}
                    />
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Table
                        className="bordered-table"
                        rowKey={(r) => r.tripId}
                        columns={columns}
                        dataSource={rows}
                        pagination={false}
                    />
                </Col>
            </Row>
        </div>
    );
};
